/**
 * The publisher's identity, as the LCEN requires it to be published.
 *
 * **Every value below is a fact about a real company, and inventing one would
 * be worse than leaving it blank.** A mentions légales block with a plausible
 * but wrong SIREN reads as complete, so nobody checks it; an obviously empty
 * one gets filled. `incompleteLegalFields()` exists so a gap is visible in the
 * interface rather than only in this file, and `TO_FILL` is what a new field
 * starts as. Every one of them is filled today, and the test asserts that —
 * a field emptied by a refactor fails the build rather than appearing as a
 * warning on the live site.
 *
 * Article 6 III of the LCEN (loi pour la confiance dans l'économie numérique)
 * requires, for a service published by a company: the company name, its legal
 * form, the address of its registered office, its share capital, a telephone
 * number or e-mail address, its registration number, its VAT number where it
 * has one, the name of the director of publication, and the name and address of
 * the host.
 */

/**
 * Marks a value the publisher must supply. Never replaced with a guess.
 *
 * Unused today because nothing is outstanding. Kept, because the next field
 * this notice needs should start here rather than at a plausible placeholder.
 */
export const TO_FILL = '';

export interface LegalNotice {
  readonly publisher: string;
  readonly legalForm: string;
  readonly shareCapital: string;
  readonly address: string;
  readonly siren: string;
  /** The registry and the number, as the LCEN asks for them together. */
  readonly rcs: string;
  /** The establishment's number. SIREN plus the five-digit NIC of the siège. */
  readonly siret: string;
  readonly vatNumber: string;
  readonly publicationDirector: string;
  readonly contactEmail: string;
  readonly privacyEmail: string;
  readonly host: string;
}

/*
  Read from the company's own extrait Kbis, dated 9 September 2026, issued by
  the greffe du Tribunal Mixte de Commerce de Pointe-à-Pitre (n° de gestion
  2026B02392). Not recalled, not inferred from a domain name, and not rounded
  into a tidier shape: the registry's spelling is the one that has to match.

  `vatNumber` came separately, because no Kbis carries one. Its key digits are
  computable from the SIREN, and `legal-notice.test.ts` checks that they agree
  — but note what that check does and does not prove. It proves the number is
  well formed for this company. It cannot prove the company is assujettie at
  all: a business under the franchise en base would compute the same string and
  have no right to publish it, and Guadeloupe sits outside the EU's VAT
  territory, so the metropolitan default is not the answer either. That this
  number exists is a fact the publisher supplied, not one derived here.
*/
export const LEGAL_NOTICE: LegalNotice = {
  publisher: 'GSI Consulting',
  legalForm: 'Société par actions simplifiée à associé unique (SASU)',
  shareCapital: '1 000 €',
  address: '17 Domaine la Riviere aux Bambous, 97170 Petit-Bourg, Guadeloupe',
  siren: '109 804 039',
  rcs: 'RCS Pointe-à-Pitre 109 804 039',
  siret: '109 804 039 00013',
  vatNumber: 'FR32109804039',
  publicationDirector: 'Guillaume Mugerin',
  contactEmail: 'guillaume@gsiconsulting.fr',
  // The same address, deliberately. Article 13 of the RGPD requires a means of
  // contact for exercising rights, not a dedicated mailbox, and a `dpo@` that
  // nobody reads is worse than the address of somebody who answers. Split it
  // the day a designated DPO exists.
  privacyEmail: 'guillaume@gsiconsulting.fr',
  // Known, because we chose it: the whole service is one Cloudflare Worker.
  host: 'Cloudflare, Inc. — 101 Townsend St, San Francisco, CA 94107, USA',
};

/**
 * Retention periods quoted to the data subject.
 *
 * These are the schema's defaults, written here in words. They must agree with
 * `tenant_ai_settings` in migration 0011 — a privacy notice that promises a
 * period the job does not apply is the problem this whole area exists to avoid.
 */
export const PRIVACY = {
  /** `transcript_retention_days` default 0: not retained at all. */
  transcriptDefault: 'aucune conservation',
  /** `customer_retention_days` default 1095. */
  customerDefault: 'trois ans',
} as const;

const REQUIRED: readonly (readonly [keyof LegalNotice, string])[] = [
  ['legalForm', 'forme juridique'],
  ['shareCapital', 'capital social'],
  ['address', 'siège social'],
  ['siren', 'SIREN'],
  ['rcs', 'immatriculation au RCS'],
  ['vatNumber', 'TVA intracommunautaire'],
  ['publicationDirector', 'directeur de la publication'],
  ['contactEmail', 'adresse de contact'],
  ['privacyEmail', 'contact « données personnelles »'],
];

/** Which mandatory fields are still unfilled. Empty means the notice is complete. */
export function incompleteLegalFields(notice: LegalNotice = LEGAL_NOTICE): readonly string[] {
  return REQUIRED.filter(([key]) => notice[key].trim() === '').map(([, label]) => label);
}
